import React, { useState, useRef, useEffect } from 'react';

const useInView = (threshold = 0.1) => {
const ref = useRef(null);
const [inView, setInView] = useState(false);
useEffect(() => {
const observer = new IntersectionObserver(([entry]) => {
if (entry.isIntersecting) setInView(true);
}, { threshold });
if (ref.current) observer.observe(ref.current);
return () => observer.disconnect();
}, [threshold]);
return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
const [ref, inView] = useInView();
return (
<div ref={ref} className={className} style={{
opacity: inView ? 1 : 0,
transform: inView ? 'translateY(0)' : 'translateY(40px)',
transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
}}>
{children}
</div>
);
};

const RutPage = ({ onNavigate }) => {
const [activeFaq, setActiveFaq] = useState(null);

const faqs = [
{ q: "Hur uppfyller jag villkoren för RUT-avdrag?", a: "Du måste vara folkbokförd i Sverige och ha betalat eller förväntas betala tillräckligt med skatt under året. Arbetet måste utföras i eller i nära anslutning till din bostad." },
{ q: "Kan jag använda RUT om jag är pensionär?", a: "Ja! Pensionärer kan använda RUT-avdrag precis som alla andra, så länge du betalar skatt i Sverige." },
{ q: "Vad händer om jag redan använt 75 000 kr?", a: "Om du nått maxbeloppet för året kan du inte göra fler RUT-avdrag förrän nästa år. Vi informerar dig om detta i bokningsprocessen." },
{ q: "Hur vet jag att ni ansöker om RUT korrekt?", a: "Vi är ett registrerat städföretag och hanterar all RUT-administration via Skatteverkets system. Du kan alltid kontrollera dina avdrag på Skatteverkets hemsida." },
{ q: "Gäller RUT för kontorsstädning?", a: "Nej, RUT gäller endast för städning i privata bostäder. Kontorsstädning och städning i kommersiella lokaler omfattas inte av RUT-avdraget." },
{ q: "Hur lång tid tar det innan Skatteverket betalar?", a: "Vi ansöker om RUT-avdraget direkt när jobbet är slutfört. Skatteverket betalar vanligtvis ut pengarna till oss inom några dagar till veckor." }
];

return (
<div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>
{/* Hero */}
<section className="relative py-32" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
<div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
<div className="relative z-10 text-center text-white px-4">
<p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>RUT-AVDRAGET</p>
<h1 className="text-5xl md:text-6xl font-bold mb-6">
Betala bara <span style={{ color: '#C9A84C' }}>hälften</span>
</h1>
<p className="text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }}>
Med RUT-avdraget betalar du bara 50% av arbetskostnaden för städning. Vi sköter allt med Skatteverket.
</p>
<div className="mt-8 inline-flex items-center gap-4 px-8 py-4 rounded-2xl"
style={{ backgroundColor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}>
<div className="text-center">
<p className="text-4xl font-black" style={{ color: '#C9A84C' }}>50%</p>
<p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Du betalar</p>
</div>
<div className="text-3xl text-white">+</div>
<div className="text-center">
<p className="text-4xl font-black" style={{ color: '#4ade80' }}>50%</p>
<p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Staten betalar</p>
</div>
<div className="text-3xl text-white">=</div>
<div className="text-center">
<p className="text-4xl font-black text-white">100%</p>
<p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Vi får betalt</p>
</div>
</div>
</div>
</section>

{/* Hur det fungerar */}
<section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>HUR DET FUNGERAR</p>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Enkelt för dig</h2>
<p className="text-lg max-w-2xl mx-auto" style={{ color: '#666' }}>
Du behöver inte göra något extra. Vi ansöker om RUT-avdraget direkt från Skatteverket åt dig.
</p>
<div className="w-16 h-1 mx-auto mt-4" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>

<div className="grid md:grid-cols-4 gap-6 mb-16">
{[
{ step: '1', title: 'Du bokar', desc: 'Välj tjänst och datum i vår app. Priset efter RUT visas direkt.' },
{ step: '2', title: 'Vi städar', desc: 'Vårt elite-team utför städningen med sjukhusstandard.' },
{ step: '3', title: 'Du betalar 50%', desc: 'Du betalar bara hälften av arbetskostnaden till oss.' },
{ step: '4', title: 'Vi får resten', desc: 'Vi ansöker om de resterande 50% direkt från Skatteverket.' }
].map((item, idx) => (
<AnimatedSection key={idx} delay={idx * 0.1} className="text-center">
<div className="w-16 h-16 rounded-full flex items-center justify-center font-bold text-2xl mx-auto mb-4"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
{item.step}
</div>
<h3 className="font-bold text-lg mb-2" style={{ color: '#1a1a2e' }}>{item.title}</h3>
<p className="text-sm" style={{ color: '#777' }}>{item.desc}</p>
</AnimatedSection>
))}
</div>

{/* Räkneexempel */}
<AnimatedSection>
<div className="p-8 rounded-2xl" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
<h3 className="text-2xl font-bold mb-6 text-center" style={{ color: '#1a1a2e' }}>Räkneexempel</h3>
<div className="grid md:grid-cols-3 gap-6">
{[
{ name: 'Standardstädning', sqm: '65 kvm', total: '3 990', youPay: '1 995', state: '1 995' },
{ name: 'Djupstädning', sqm: '80 kvm', total: '5 990', youPay: '2 995', state: '2 995' },
{ name: 'Flyttstädning', sqm: '100 kvm', total: '8 500', youPay: '4 250', state: '4 250' }
].map((ex, idx) => (
<div key={idx} className="p-6 rounded-xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
<h4 className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{ex.name}</h4>
<p className="text-sm mb-4" style={{ color: '#888' }}>{ex.sqm}</p>
<div className="space-y-2 text-sm">
<div className="flex justify-between">
<span style={{ color: '#888' }}>Totalt pris</span>
<span className="font-bold line-through" style={{ color: '#aaa' }}>{ex.total} kr</span>
</div>
<div className="flex justify-between">
<span style={{ color: '#888' }}>Staten betalar</span>
<span className="font-bold" style={{ color: '#16a34a' }}>{ex.state} kr</span>
</div>
<div className="flex justify-between pt-2" style={{ borderTop: '1px solid #e8e4d9' }}>
<span className="font-bold" style={{ color: '#1a1a2e' }}>Du betalar</span>
<span className="font-bold text-lg" style={{ color: '#C9A84C' }}>{ex.youPay} kr</span>
</div>
</div>
</div>
))}
</div>
</div>
</AnimatedSection>
</div>
</section>

{/* Vilka tjänster */}
<section className="py-24 px-4" style={{ backgroundColor: '#1a1a2e' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>TJÄNSTER SOM OMFATTAS</p>
<h2 className="text-4xl font-bold mb-4 text-white">Vad ingår i RUT?</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="grid md:grid-cols-2 gap-8">
<AnimatedSection delay={0.1}>
<div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)' }}>
<h3 className="text-xl font-bold mb-6" style={{ color: '#4ade80' }}>✅ Omfattas av RUT</h3>
<ul className="space-y-3">
{['Hemstädning', 'Storstädning / Djupstädning', 'Flyttstädning', 'Fönsterputsning', 'Tvättning & strykning', 'Trädgårdsarbete'].map(item => (
<li key={item} className="flex items-center gap-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
<span style={{ color: '#4ade80' }}>✓</span>{item}
</li>
))}
</ul>
</div>
</AnimatedSection>
<AnimatedSection delay={0.2}>
<div className="p-8 rounded-2xl" style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
<h3 className="text-xl font-bold mb-6" style={{ color: '#f87171' }}>❌ Omfattas INTE av RUT</h3>
<ul className="space-y-3">
{['Kontorsstädning', 'Material och städprodukter', 'Resekostnader', 'Kommersiella lokaler', 'Städutrustning'].map(item => (
<li key={item} className="flex items-center gap-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
<span style={{ color: '#f87171' }}>✗</span>{item}
</li>
))}
</ul>
</div>
</AnimatedSection>
</div>

<AnimatedSection delay={0.3}>
<div className="mt-8 p-6 rounded-2xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.3)' }}>
<p className="text-lg mb-2 text-white">Maxbelopp per person och år</p>
<p className="text-5xl font-black mb-2" style={{ color: '#C9A84C' }}>75 000 kr</p>
<p style={{ color: 'rgba(255,255,255,0.6)' }}>RUT och ROT räknas ihop. Max 50 000 kr av detta får vara ROT-avdrag.</p>
</div>
</AnimatedSection>
</div>
</section>

{/* FAQ */}
<section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
<div className="max-w-3xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VANLIGA FRÅGOR</p>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Frågor om RUT</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="space-y-4">
{faqs.map((faq, idx) => (
<AnimatedSection key={idx} delay={idx * 0.05}>
<div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e8e4d9' }}>
<button onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
className="w-full p-6 text-left flex justify-between items-center transition-all"
style={{ backgroundColor: activeFaq === idx ? '#1a1a2e' : '#fff' }}>
<span className="font-semibold" style={{ color: activeFaq === idx ? '#fff' : '#1a1a2e' }}>{faq.q}</span>
<span className="text-2xl ml-4" style={{ color: '#C9A84C' }}>{activeFaq === idx ? '−' : '+'}</span>
</button>
{activeFaq === idx && (
<div className="px-6 pb-6 pt-2" style={{ backgroundColor: '#f8f7f4' }}>
<p style={{ color: '#555' }}>{faq.a}</p>
</div>
)}
</div>
</AnimatedSection>
))}
</div>
</div>
</section>

{/* CTA */}
<section className="py-24 px-4" style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-3xl mx-auto text-center">
<AnimatedSection>
<h2 className="text-4xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Redo att boka med 50% RUT?</h2>
<p className="text-lg mb-8" style={{ color: '#666' }}>
Vi sköter allt med Skatteverket. Du betalar bara hälften.
</p>
<div className="flex flex-col sm:flex-row gap-4 justify-center">
<button onClick={() => onNavigate('booking')}
className="px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
Boka Nu
</button>
<a href="https://www.skatteverket.se/rutavdrag" target="_blank" rel="noopener noreferrer"
className="px-10 py-4 rounded-full font-bold text-lg border-2 transition-all hover:scale-105"
style={{ borderColor: '#e8e4d9', color: '#555' }}>
Skatteverkets hemsida →
</a>
</div>
</AnimatedSection>
</div>
</section>
</div>
);
};

export default RutPage;